/* =============================================================================
 * posthog-utils.js
 * Shared helpers for TIDLOS PostHog instrumentation.
 *
 * Loaded once in theme.liquid <head> via:
 *   <script src="{{ 'posthog-utils.js' | asset_url }}" defer></script>
 *
 * Exposes on window:
 *   - hashEmail(email)     — SHA-256 of normalized email; returns 64-char lowercase hex
 *   - safeCapture(name, props) — wraps posthog.capture in try/catch so a single bad
 *                                listener cannot break the existing Product Added flow
 *   - debounce(fn, ms)     — light debounce helper
 *
 * Normalization rule (must match Liquid + Snowflake):
 *   - Lowercase the entire string
 *   - Trim leading/trailing whitespace
 *   - SHA-256
 *   - Output as lowercase hex
 *
 * Convergence references:
 *   Liquid:    {{ customer.email | downcase | strip | sha256 }}
 *   Snowflake: SHA2_HEX(LOWER(TRIM(customer_email)), 256)
 *   JS (this): hashEmail(email)
 *
 * Test vector:
 *   hashEmail('test@example.com')
 *   → '973dfe463ec85785f5f95af5ba3906eedb2d931c24e69824a89ea65dba4e813b'
 * ============================================================================= */

(function () {
  'use strict';

  async function hashEmail(email) {
    if (!email || typeof email !== 'string') return null;
    var normalized = email.toLowerCase().trim();
    if (!normalized) return null;
    try {
      var bytes = new TextEncoder().encode(normalized);
      var hashBuffer = await crypto.subtle.digest('SHA-256', bytes);
      var hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(function (b) { return b.toString(16).padStart(2, '0'); }).join('');
    } catch (e) {
      // crypto.subtle is HTTPS-only; localhost http will fail.
      // In production this should never happen. Log and return null.
      if (window.console && console.warn) {
        console.warn('[posthog-utils] hashEmail failed:', e);
      }
      return null;
    }
  }

  function safeCapture(eventName, properties) {
    try {
      if (window.posthog && typeof window.posthog.capture === 'function') {
        window.posthog.capture(eventName, properties || {});
      }
    } catch (e) {
      // Never let an event capture failure poison page execution.
      if (window.console && console.warn) {
        console.warn('[posthog-utils] capture failed for', eventName, e);
      }
    }
  }

  function safeIdentify(distinctId, userProperties) {
    try {
      if (window.posthog && typeof window.posthog.identify === 'function' && distinctId) {
        window.posthog.identify(distinctId, userProperties || {});
      }
    } catch (e) {
      if (window.console && console.warn) {
        console.warn('[posthog-utils] identify failed:', e);
      }
    }
  }

  function debounce(fn, ms) {
    var timer = null;
    return function debounced() {
      var ctx = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  // Expose on window for cross-file use
  window.hashEmail = hashEmail;
  window.safeCapture = safeCapture;
  window.safeIdentify = safeIdentify;
  window.tidlosDebounce = debounce;

  // Lightweight self-test on load (writes to console only — no events captured).
  // Drew can run `window.hashEmail('test@example.com')` in browser console to verify.
  // Expected output: '973dfe463ec85785f5f95af5ba3906eedb2d931c24e69824a89ea65dba4e813b'
})();
